import { useQuery, useMutation } from '@tanstack/react-query'
import { getMerchantProfile } from '../../services/merchant'
import { getActiveCampaigns } from '../../services/campaign'
import { getRewards } from '../../services/reward'
import { 
  menuItemsByMerchant, 
  getMenuCategories,
  getMenuItemsByCategory 
} from '../../services/menu'
import { 
  login,
  register,
} from '../../services/auth'
import {
  addOrder,
  getOrderHistory,
  getOrderStatus
} from '../../services/order'

import {
  MENU_CATEGORY_KEY,
  MENU_ITEMS_KEY,
  MERCHANT_ITEMS_KEY,
  MERCHANT_ACCOUNT_KEY,
  ORDER_HISTORY_KEY,
  ORDER_STATUS_KEY
} from '../../constants/querykeys'

// Merchant Queries
export function GetMerchantProfile(merchantId) {
  return useQuery({
    queryKey: [MERCHANT_ACCOUNT_KEY, merchantId],
    queryFn: () => getMerchantProfile(merchantId),
    enabled: !!merchantId,
    select: (res) => res?.data?.restaurant,
    staleTime: 5 * 60 * 1000,
  })
}

// Menu Queries
export function GetMenuItemsByMerchant(merchantId) {
  return useQuery({
    queryKey: [MERCHANT_ITEMS_KEY, merchantId],
    queryFn: () => menuItemsByMerchant(merchantId),
    enabled: !!merchantId,
    select: (res) => res?.data?.menuCategories || [],
    staleTime: 5 * 60 * 1000,
  })
}

export function GetMenuCategories(merchantId) {
  return useQuery({
    queryKey: [MENU_CATEGORY_KEY, merchantId],
    queryFn: () => getMenuCategories(merchantId),
    enabled: !!merchantId,
    select: (res) => res?.data?.menuCategories || res?.data?.categories || [],
    staleTime: 5 * 60 * 1000,
  })
}

export function GetMenuItemsByCategory(merchantId, categoryId) {
  return useQuery({
    queryKey: [MENU_ITEMS_KEY, merchantId, categoryId],
    queryFn: () => getMenuItemsByCategory(merchantId, categoryId),
    enabled: !!merchantId && !!categoryId,
    select: (res) => res?.data?.menuItems || res?.data?.items || [],
    staleTime: 5 * 60 * 1000,
  })
}

// Auth Mutations
export function useLogin() {
  return useMutation({
    mutationFn: (data) => login(data),
  })
}

export function useRegister() {
  return useMutation({
    mutationFn: (data) => register(data),
  })
}

// Order Mutations and Queries
export function useAddOrder() {
  return useMutation({
    mutationFn: (data) => addOrder(data),
    // onSuccess toast removed — the PaymentDrawer handles success/error messaging
    // to avoid double-toast and to validate the response before showing success.
  })
}

export function GetOrderHistory(merchantId, customerId) {
  return useQuery({
    queryKey: [ORDER_HISTORY_KEY, merchantId, customerId],
    queryFn: () => getOrderHistory(merchantId, customerId),
    enabled: !!merchantId && !!customerId,
    select: (res) => res?.data?.orders || [],
  })
}

export function GetOrderStatus(merchantId, orderId) {
  return useQuery({
    queryKey: [ORDER_STATUS_KEY, merchantId, orderId],
    queryFn: () => getOrderStatus(merchantId, orderId),
    enabled: !!merchantId && !!orderId,
    refetchInterval: 5000,
    select: (res) => res?.data?.order || res?.data,
  })
}

export function GetActiveCampaigns(merchantId) {
  return useQuery({
    queryKey: ['activeCampaigns', merchantId],
    queryFn: () => getActiveCampaigns(merchantId),
    enabled: !!merchantId,
    select: (res) => res?.data?.campaigns || [],
    staleTime: 5 * 60 * 1000,
  })
}

export function GetRewards(merchantId) {
  return useQuery({
    queryKey: ['rewards', merchantId],
    queryFn: () => getRewards(merchantId),
    enabled: !!merchantId,
    select: (res) => res?.data?.rewards || [],
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  })
}
